'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { authClient } from '@/lib/auth/client';
import {
  loginSchema,
  type LoginFormValues,
} from '@/features/auth/types/auth-schemas';
import { SocialLoginButtons } from './social-login-buttons';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

// ── Role-based redirect helper ──────────────────────────────────

function getRedirectPath(role: string | null | undefined): string {
  if (!role || role === 'user') return '/role-select';
  if (role.includes('producer') || role.includes('org_admin'))
    return '/dashboard';
  if (role.includes('vendor')) return '/vendor/dashboard';
  if (role.includes('platform_admin')) return '/platform-admin/dashboard';
  return '/role-select';
}

// ── Inline error alert ──────────────────────────────────────────

interface InlineErrorProps {
  message: string;
  action?: React.ReactNode;
}

function InlineError({ message, action }: InlineErrorProps) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-destructive/20 bg-destructive/5 px-3.5 py-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <span>
        {message}
        {action && <> {action}</>}
      </span>
    </div>
  );
}

// ── Login form ──────────────────────────────────────────────────

export function LoginForm() {
  const router = useRouter();
  const t = useTranslations('auth.login');
  const [inlineError, setInlineError] = useState<React.ReactNode | null>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const isSubmitting = form.formState.isSubmitting;

  async function onSubmit(values: LoginFormValues) {
    setInlineError(null);
    try {
      const result = await authClient.signIn.email({
        email: values.email,
        password: values.password,
        rememberMe: values.rememberMe,
      });

      if (result.error) {
        const msg = result.error.message ?? '';
        const status = result.error.status ?? 0;

        if (
          status === 403 ||
          msg.toLowerCase().includes('verify') ||
          msg.toLowerCase().includes('verified')
        ) {
          setInlineError(
            <InlineError
              message={t('errors.verifyEmail')}
              action={
                <Link
                  href="/signup"
                  className="underline underline-offset-2 hover:opacity-80 transition-opacity"
                >
                  {t('errors.resendVerification')}
                </Link>
              }
            />
          );
          return;
        }

        if (
          msg.toLowerCase().includes('banned') ||
          msg.toLowerCase().includes('deactivat')
        ) {
          setInlineError(<InlineError message={t('errors.deactivated')} />);
          return;
        }

        if (
          msg.toLowerCase().includes('rate') ||
          msg.toLowerCase().includes('many attempt')
        ) {
          setInlineError(<InlineError message={t('errors.tooManyAttempts')} />);
          return;
        }

        // Generic invalid credentials
        setInlineError(
          <InlineError
            message={t('errors.invalidCredentials')}
            action={
              <Link
                href="/reset-password"
                className="underline underline-offset-2 hover:opacity-80 transition-opacity"
              >
                {t('forgotPassword')}
              </Link>
            }
          />
        );
        return;
      }

      // Success — fetch session to get properly-typed user (signIn.email return type
      // uses the base Auth<BetterAuthOptions> generic without plugin fields).
      const sessionResult = await authClient.getSession();
      const role = sessionResult.data?.user?.role;
      const redirectPath = getRedirectPath(role);

      // Use window.location.replace for full page reload to ensure server components
      // re-evaluate with the new session (avoids stale data issues)
      window.location.replace(redirectPath);
    } catch {
      toast.error(t('errors.connectionError'));
    }
  }

  return (
    <div className="space-y-6 animate-scale-in">
      {/* Header */}
      <div className="space-y-1.5">
        <h1 className="font-display text-2xl font-bold text-foreground">
          {t('title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('subtitleFull')}</p>
      </div>

      {/* Social login */}
      <SocialLoginButtons callbackURL="/role-select" />

      {/* Divider */}
      <div className="flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-xs text-muted-foreground shrink-0">
          {t('orContinueWith')}
        </span>
        <Separator className="flex-1" />
      </div>

      {/* Inline error area */}
      {inlineError && <div>{inlineError}</div>}

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Email */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('emailLabel')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t('emailPlaceholder')}
                    autoComplete="email"
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Password */}
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('passwordLabel')}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t('passwordPlaceholder')}
                    autoComplete="current-password"
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Remember me + Forgot password row */}
          <div className="flex items-center justify-between">
            <FormField
              control={form.control}
              name="rememberMe"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox
                      id="remember-me"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="remember-me"
                    className="text-sm font-normal cursor-pointer"
                  >
                    {t('rememberMe')}
                  </FormLabel>
                </FormItem>
              )}
            />

            <Link
              href="/reset-password"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('forgotPassword')}
            </Link>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            className="w-full h-11 gap-2 font-semibold transition-transform hover:-translate-y-0.5 duration-200"
            disabled={isSubmitting}
          >
            {isSubmitting && <Spinner className="size-4" />}
            {isSubmitting ? t('loading') : t('submitButton')}
          </Button>
        </form>
      </Form>

      {/* Footer */}
      <p className="text-center text-sm text-muted-foreground">
        {t('noAccount')}{' '}
        <Link
          href="/signup"
          className="font-medium text-primary hover:text-primary/80 transition-colors"
        >
          {t('signUp')}
        </Link>
      </p>
    </div>
  );
}
